import get from 'lodash/get'
import type { Table } from '@wmt/content-blocks'
import type { PlayerRoster } from '~/types'
import { toFloatByPath } from '~/statistics/utils/toFloatByPath'
import { defaultBodyCells, defaultColumns } from '~/statistics/playerRosters/aliases'
import { getSortAsNumber } from '~/shared/lib/sorts'
import { StatisticRootKey } from '~/enums/statistic-root-key'
import type { CellsReturnDataType } from '~/statistics/playerRosters/index'
import type { IndividualTableSchema } from '~/statistics/team/individual'

export const groupColumns: Table.Cell<PlayerRoster.Model>[] = [
  { content: '', colspan: 3 },
  { content: 'Tackles', colspan: 4 },
  { content: 'Sacks', colspan: 2 },
  { content: 'Pass Defense', colspan: 3 },
  { content: 'Fumbles', colspan: 3 },
  { content: 'Misc', colspan: 2 },
]

export const columns: Table.Cell<PlayerRoster.Model>[] = [
  { content: 'SOLO', sort: true },
  { content: 'A', sort: true },
  { content: 'Tot', sort: true },
  { content: 'TFL - Yds', sort: true },
  { content: 'No', sort: true },
  { content: 'Yds', sort: true },
  { content: 'Int', sort: true },
  { content: 'IRYds', sort: true },
  { content: 'BrUp', sort: true },
  { content: 'FF', sort: true },
  { content: 'FR', sort: true },
  { content: 'Yds', sort: true },
  { content: 'BK', sort: true },
  { content: 'Saf', sort: true },
]

export const cells = (type: StatisticRootKey = StatisticRootKey.Statistics): CellsReturnDataType => ([
  { content: `$get(${type}.defense.tackles, 0)` }, // UA
  { content: `$get(${type}.defense.assists, 0)` }, // A
  {
    content: (player: PlayerRoster.Model) => toFloatByPath(player, `${type}.defense.combined`),
    onSort: getSortAsNumber,
  }, // Tot
  {
    content: (player: PlayerRoster.Model) => {
      const tLoss = toFloatByPath(player, `${type}.defense.tloss`, 1)
      const tLossYards = get(player, `${type}.defense.tlossYards`, 0)

      return `${tLoss} - ${tLossYards}`
    }, // TFL - Yds
  },
  { content: (player: PlayerRoster.Model) => toFloatByPath(player, `${type}.defense.sacks`), onSort: getSortAsNumber }, // No
  { content: `$get(${type}.defense.sackYards, 0)` }, // Yds
  { content: `$get(${type}.defense.interceptions, 0)` }, // Int
  { content: `$get(${type}.intReturns.yards, 0)` }, // IRYds
  { content: `$get(${type}.defense.passesDefended, 0)` }, // BrUp
  { content: `$get(${type}.fumbles.forcedFumbles, 0)` }, // FF
  { content: `$get(${type}.fumbles.oppRec, 0)` }, // FR
  { content: `$get(${type}.fumbles.oppRecYards, 0)` }, // Yds (for oppRec)
  { content: `$get(${type}.defense.spBlocks, 0)` }, // BK
  { content: `$get(${type}.defense.safeties, 0)` }, // Saf
])

export const playerRosterDefenseStatsSchema: IndividualTableSchema = {
  filter: player => Boolean(player.statistics?.defense) && Boolean(player.statistics?.defense?.combined ?? false),
  schema: {
    head: {
      rows: [
        {
          cells: groupColumns,
        },
        {
          cells: [
            ...defaultColumns,
            ...columns,
          ],
        },
      ],
    },
    body: {
      rows: {
        cells: [
          ...defaultBodyCells,
          ...cells() as Table.BodyCell<PlayerRoster.Model>[],
        ],
      },
    },
  },
}
